import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db, auth, functions } from "./firebase";
import { httpsCallable } from "firebase/functions";
import GraderDashboardLayout from "./GraderDashboardLayout";

function GraderReview() {
  const { submissionId } = useParams();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [authenticated, setAuthenticated] = useState(false);
  const [graderUid, setGraderUid] = useState(null);

  // Anticipated/Ceiling are dropdowns 1–10
  const [anticipatedGrade, setAnticipatedGrade] = useState("");
  const [ceilingGrade, setCeilingGrade] = useState("");

  // Centering stays free text (e.g., "55/45")
  const [centeringFront, setCenteringFront] = useState("");
  const [centeringBack, setCenteringBack] = useState("");

  // Estimated value is STRING (allow ranges, e.g. "$30–$50")
  const [gradedValue, setGradedValue] = useState("");

  // ✅ Updated checklist
  const ISSUE_OPTIONS = [
    "centering",
    "edge whitening",
    "edge damage",
    "corner whitening",
    "corner damage",
    "corner cut",
    "surface damage",
    "surface scratching",
    "surface dents",
  ];
  const [issues, setIssues] = useState([]);

  // ✅ “Empty checkbox” for a custom note
  const [customIssueChecked, setCustomIssueChecked] = useState(false);
  const [customIssueText, setCustomIssueText] = useState("");

  const [cancelReason, setCancelReason] = useState("");
  const [showCancelPrompt, setShowCancelPrompt] = useState(false);

  const navigate = useNavigate();

  // Auth gate
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        navigate("/login");
      } else {
        setAuthenticated(true);
        setGraderUid(user.uid);
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Load submission (and best-effort claim)
  useEffect(() => {
    if (!authenticated || !submissionId || !graderUid) return;

    const fetchSubmission = async () => {
      try {
        const ref = doc(db, "submissions", submissionId);
        const snap = await getDoc(ref);

        if (!snap.exists()) {
          setSubmission(null);
          return;
        }

        const data = { id: snap.id, ...snap.data() };
        setSubmission(data);

        // Best-effort claim
        if (!data.inProgressBy) {
          try {
            await updateDoc(ref, { inProgressBy: graderUid });
            setSubmission((prev) => (prev ? { ...prev, inProgressBy: graderUid } : prev));
          } catch (claimErr) {
            console.warn("Best-effort claim failed:", claimErr?.message || claimErr);
          }
        }
      } catch (err) {
        console.error("Error loading submission:", err?.message || err);
      } finally {
        setLoading(false);
      }
    };

    fetchSubmission();
  }, [submissionId, graderUid, authenticated]);

  const toggleIssue = (label) => {
    setIssues((prev) =>
      prev.includes(label) ? prev.filter((i) => i !== label) : [...prev, label]
    );
  };

  const handleSubmitReview = async () => {
    if (!anticipatedGrade || !ceilingGrade) {
      setMessage("Please select Anticipated and Ceiling grades (1–10).");
      return;
    }

    // If the “empty checkbox” is checked, require a note
    if (customIssueChecked && customIssueText.trim() === "") {
      setMessage("Add a note for the custom issue or uncheck the custom box.");
      return;
    }

    const finalIssues =
      customIssueChecked && customIssueText.trim() !== ""
        ? [...issues, customIssueText.trim()]
        : issues;

    setSubmitting(true);
    try {
      const ref = doc(db, "submissions", submissionId);
      const snap = await getDoc(ref);
      if (!snap.exists()) return;

      const data = snap.data();
      const existingReviews = Array.isArray(data.reviews) ? data.reviews : [];

      const alreadyReviewed = existingReviews.some((r) => r.grader === graderUid);
      if (alreadyReviewed) {
        setMessage("You’ve already submitted a review.");
        setSubmitting(false);
        return;
      }

      const updatedReviews = [
        ...existingReviews,
        {
          grader: graderUid,
          anticipatedGrade: Number(anticipatedGrade),
          ceilingGrade: Number(ceilingGrade),
          centeringFront: centeringFront.trim() || null,
          centeringBack: centeringBack.trim() || null,
          gradedValue: gradedValue.trim() !== "" ? gradedValue.trim() : null, // keep string
          issues: finalIssues,
          timestamp: new Date().toISOString(),
        },
      ];

      await updateDoc(ref, {
        reviews: updatedReviews,
        status: "COMPLETE",
        inProgressBy: null,
      });

      setMessage("Review submitted!");
      navigate("/grader-dashboard");
    } catch (err) {
      console.error("Error submitting review:", err?.message || err);
      setMessage("This action is currently restricted. An admin function will handle reviews.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleForfeit = async () => {
    try {
      const ref = doc(db, "submissions", submissionId);
      await updateDoc(ref, { inProgressBy: null });
      navigate("/grader-dashboard");
    } catch {
      navigate("/grader-dashboard");
    }
  };

  const handleCancelSubmission = async () => {
    if (!cancelReason.trim()) {
      alert("Please enter a reason for cancellation.");
      return;
    }
    try {
      const cancelFn = httpsCallable(functions, "cancelSubmissionWithRefund");
      await cancelFn({ submissionId, reason: cancelReason.trim() });
      setMessage("Submission cancelled and credit refunded.");
      navigate("/grader-dashboard");
    } catch (err) {
      alert("Error cancelling submission: " + (err?.message || String(err)));
    }
  };

  const openCenteringTool = () => {
    window.open("/centering-tool", "_blank", "noopener,noreferrer");
  };

  if (!authenticated || loading) return <p>Loading...</p>;
  if (!submission) return <p>Submission not found.</p>;

  const anotherGraderIsWorking =
    submission.inProgressBy && submission.inProgressBy !== graderUid;

  const renderGradeOptions = () => (
    <>
      <option value="">Select a grade</option>
      {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
        <option key={n} value={n}>
          {n}
        </option>
      ))}
    </>
  );

  return (
    <GraderDashboardLayout>
      <div style={{ width: "100%", overflowX: "hidden" }}>
        <div
          style={{
            padding: "20px",
            maxWidth: "700px",
            width: "100%",
            margin: "0 auto",
            boxSizing: "border-box",
            overflowWrap: "anywhere",
          }}
        >
          <h2>Review Submission: {submissionId}</h2>
          <p>
            <strong>Card Name:</strong> {submission.cardName}
          </p>
          <p>
            <strong>Card Identifier:</strong> {submission.cardNumber || "N/A"}
          </p>

          {anotherGraderIsWorking && (
            <p style={{ color: "red" }}>Another grader is currently reviewing this submission.</p>
          )}

          <div>
            <h3>Images</h3>
            {submission.images ? (
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "10px" }}>
                {Object.entries(submission.images).map(([label, url]) => (
                  <div key={label}>
                    <img
                      src={url}
                      alt={label}
                      style={{
                        display: "block",
                        maxWidth: "100%",
                        height: "auto",
                        borderRadius: "6px",
                        border: "1px solid #ccc",
                      }}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <p>No images submitted.</p>
            )}
          </div>

          <div>
            <h3>Submit Your Review</h3>

            {/* Grade dropdowns */}
            <label style={{ display: "block", marginBottom: "4px", fontWeight: 600 }}>
              Anticipated Grade (1–10)
            </label>
            <select
              value={anticipatedGrade}
              onChange={(e) => setAnticipatedGrade(e.target.value)}
              style={{ width: "100%", padding: "8px", marginBottom: "8px" }}
            >
              {renderGradeOptions()}
            </select>

            <label style={{ display: "block", marginBottom: "4px", fontWeight: 600 }}>
              Ceiling Grade (1–10)
            </label>
            <select
              value={ceilingGrade}
              onChange={(e) => setCeilingGrade(e.target.value)}
              style={{ width: "100%", padding: "8px", marginBottom: "8px" }}
            >
              {renderGradeOptions()}
            </select>

            {/* Centering */}
            <input
              type="text"
              placeholder="Centering Score (Front, e.g. 55/45)"
              value={centeringFront}
              onChange={(e) => setCenteringFront(e.target.value)}
              style={{ width: "100%", padding: "8px", marginBottom: "8px" }}
            />

            <input
              type="text"
              placeholder="Centering Score (Back, e.g. 60/40)"
              value={centeringBack}
              onChange={(e) => setCenteringBack(e.target.value)}
              style={{ width: "100%", padding: "8px", marginBottom: "8px" }}
            />

            {/* Estimated value as STRING to allow ranges */}
            <input
              type="text"
              placeholder='Estimated Value (e.g. "$30–$50" or "40-60")'
              value={gradedValue}
              onChange={(e) => setGradedValue(e.target.value)}
              style={{ width: "100%", padding: "8px", marginBottom: "8px" }}
            />

            {/* Issues checkboxes */}
            <div style={{ marginTop: "12px", marginBottom: "12px" }}>
              <label style={{ display: "block", marginBottom: "6px", fontWeight: 600 }}>
                Issues observed (check all that apply)
              </label>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "8px 12px",
                }}
              >
                {ISSUE_OPTIONS.map((label) => (
                  <label key={label} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <input
                      type="checkbox"
                      checked={issues.includes(label)}
                      onChange={() => toggleIssue(label)}
                    />
                    <span>{label}</span>
                  </label>
                ))}

                {/* Empty checkbox for custom note */}
                <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <input
                    type="checkbox"
                    checked={customIssueChecked}
                    onChange={(e) => {
                      setCustomIssueChecked(e.target.checked);
                      if (!e.target.checked) setCustomIssueText("");
                    }}
                  />
                  <span>Custom note</span>
                </label>

                {customIssueChecked && (
                  <input
                    type="text"
                    placeholder='Type your custom note (e.g. "print line" or "tiny stain")'
                    value={customIssueText}
                    onChange={(e) => setCustomIssueText(e.target.value)}
                    style={{ gridColumn: "1 / -1", width: "100%", padding: "8px" }}
                  />
                )}
              </div>
            </div>

            {/* ACTION BUTTONS */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "10px",
                alignItems: "center",
                marginTop: "8px",
              }}
            >
              <button
                onClick={handleSubmitReview}
                disabled={anotherGraderIsWorking || submitting}
                style={{
                  padding: "10px 20px",
                  backgroundColor: anotherGraderIsWorking || submitting ? "#ccc" : "#1976d2",
                  color: "#fff",
                  border: "none",
                  borderRadius: "5px",
                  fontWeight: "bold",
                }}
              >
                {submitting ? "Submitting..." : "Complete Review"}
              </button>

              <button
                onClick={handleForfeit}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#ccc",
                  color: "#000",
                  border: "none",
                  borderRadius: "5px",
                  fontWeight: "bold",
                }}
              >
                Forfeit Submission
              </button>

              <button
                onClick={() => setShowCancelPrompt(true)}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#d32f2f",
                  color: "#fff",
                  border: "none",
                  borderRadius: "5px",
                  fontWeight: "bold",
                }}
              >
                Cancel Submission
              </button>

              <button
                onClick={openCenteringTool}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#10b981",
                  color: "#fff",
                  border: "none",
                  borderRadius: "5px",
                  fontWeight: "bold",
                }}
              >
                Open Centering Tool
              </button>
            </div>

            {message && <p style={{ color: "green", marginTop: "10px" }}>{message}</p>}

            {showCancelPrompt && (
              <div style={{ marginTop: "15px" }}>
                <textarea
                  placeholder="Enter reason for cancellation"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  rows={3}
                  style={{ width: "100%", padding: "8px", marginBottom: "8px" }}
                />
                <button
                  onClick={handleCancelSubmission}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: "#d32f2f",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    fontWeight: "bold",
                  }}
                >
                  Confirm Cancellation
                </button>
              </div>
            )}
          </div>

          {/* Customer-facing section: always show issues */}
          <div style={{ marginTop: "20px" }}>
            <h3>Issues Cited:</h3>
            {issues.length > 0 || (customIssueChecked && customIssueText.trim() !== "") ? (
              <ul>
                {issues.map((issue) => (
                  <li key={issue}>- {issue}</li>
                ))}
                {customIssueChecked && customIssueText.trim() !== "" && (
                  <li key="__custom">- {customIssueText.trim()}</li>
                )}
              </ul>
            ) : (
              <p>None</p>
            )}
          </div>
        </div>
      </div>
    </GraderDashboardLayout>
  );
}

export default GraderReview;
